import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory, createMemoryHistory } from 'history';
import thunkMiddleware from 'redux-thunk';
import promiseMiddleware from 'redux-promise-middleware';
import get from 'lodash/get';
import { normalize } from 'normalizr';
import Immutable from 'immutable';
import merge from 'lodash/merge';
import normalizeJsonApi from './modules/normalize';
import { albumPhotoSchema, albumSchema } from './modules/albumDuck';
import { announcementSchema } from './modules/announcementDuck';
import loggerMiddleware from '../lib/middlewares/loggerMiddleware'; // eslint-disable-line
import createRootReducer, { $$initialState } from './modules';
import organizationDuck from './modules/organizationDuck';
import railsContextDuck from './modules/railsContextDuck';
import camelcaseKeys from 'camelcase-keys';
import { configureStore } from '@reduxjs/toolkit';

export default (props, railsContext) => {
  const isServer = railsContext.serverSide;
  const history = isServer
    ? createMemoryHistory({
        initialEntries: [railsContext.location],
      })
    : createBrowserHistory();

  // const enhancers = [];

  // if (process.env.NODE_ENV === 'development' && !isServer) {
  //   const devToolsExtension = window.devToolsExtension;
  //   if (typeof devToolsExtension === 'function') {
  //     enhancers.push(devToolsExtension());
  //   }
  // }

  if (get(props.$$albumStore, 'albumPhotos')) {
    const normalizedAlbumPhotos = normalize(props.$$albumStore.albumPhotos, [
      albumPhotoSchema,
    ]);
    props.$$albumStore.$$entities = normalizedAlbumPhotos.entities; //eslint-disable-line
    props.$$albumStore.$$albumPhotoIds = normalizedAlbumPhotos.result; //eslint-disable-line
    delete props.$$albumStore.albumPhotos; //eslint-disable-line
  }

  if (get(props.$$albumStore, 'albums')) {
    const normalizedAlbums = normalize(props.$$albumStore.albums, [
      albumSchema,
    ]);
    props.$$albumStore.$$entities = normalizedAlbums.entities; //eslint-disable-line
    props.$$albumStore.$$albumIds = normalizedAlbums.result; //eslint-disable-line
    delete props.$$albumStore.albums; //eslint-disable-line
  }

  if (get(props.$$announcementStore, 'announcement')) {
    const normalizedAnnouncement = normalize(
      props.$$announcementStore.announcement,
      announcementSchema
    );
    props.$$announcementStore.$$entities = normalizedAnnouncement.entities; //eslint-disable-line
    props.$$announcementStore.announcementId = normalizedAnnouncement.result; //eslint-disable-line
    delete props.$$announcementStore.announcement; //eslint-disable-line
  }

  props.$$organizationStore = props.$$organizationStore || {};
  props.$$organizationStore.$$organization = railsContext.organization || {};

  const middleware = [
    thunkMiddleware,
    routerMiddleware(history),
    loggerMiddleware,
    promiseMiddleware,
  ];

  const $$mergedInitialState = $$initialState
    .mergeDeep(Immutable.fromJS(props))
    .mergeDeep({ $$railsContextStore: railsContext })
    .update('$$railsContextStore', ($$railsContextStore) =>
      $$railsContextStore.mapKeys((key) => {
        switch (key) {
          case 'currentUser':
            return '$$currentUser';
          case 'ckeditorPlugins':
            return '$$ckeditorPlugins';
          case 'flash':
            return '$$flash';
          case 'organization':
            return '$$organization';
          default:
            return key;
        }
      })
    );

  const mergedInitialState = $$mergedInitialState.toObject();

  mergedInitialState.appStore = merge(
    mergedInitialState.appStore,
    props.appStore
  );

  if (railsContext.jsonapi.currentOrganization) {
    const normalizedCurrentOrganization = normalizeJsonApi(
      railsContext.jsonapi.currentOrganization
    );
    mergedInitialState.jsonApiStore.organization =
      normalizedCurrentOrganization.organization;
    mergedInitialState.jsonApiStore['CURRENT_ORGANIZATION'] = {
      result: normalizedCurrentOrganization.result,
    };
    mergedInitialState.jsonApiStoreV2.entities.organization =
      normalizedCurrentOrganization.organization;
    mergedInitialState.jsonApiStoreV2.results['CURRENT_ORGANIZATION'] =
      normalizedCurrentOrganization.result;
  }

  if (railsContext.jsonapi.currentUser) {
    const normalizedCurrentUser = normalizeJsonApi(
      railsContext.jsonapi.currentUser
    );
    mergedInitialState.jsonApiStore.user = normalizedCurrentUser.user;
    mergedInitialState.jsonApiStore['CURRENT_USER'] = {
      result: normalizedCurrentUser.result,
    };
    mergedInitialState.jsonApiStoreV2.entities.user =
      normalizedCurrentUser.user;
    mergedInitialState.jsonApiStoreV2.results['CURRENT_USER'] =
      normalizedCurrentUser.result;
  }

  delete railsContext.jsonapi;

  if (props.jsonapi) {
    Object.keys(props.jsonapi).forEach((namespace) => {
      const { result, ...types } = normalizeJsonApi(props.jsonapi[namespace]);
      mergedInitialState.jsonApiStoreV2.results[namespace] = result;
      mergedInitialState.jsonApiStoreV2.entities = merge(
        mergedInitialState.jsonApiStoreV2.entities,
        types
      );
      mergedInitialState.jsonApiStore[namespace] = { result };
      mergedInitialState.jsonApiStore = merge(
        mergedInitialState.jsonApiStore,
        types
      );
    });
    delete mergedInitialState.jsonapi;
  }

  mergedInitialState.appStore.railsContext = Object.assign({}, railsContext);
  delete mergedInitialState.appStore.railsContext.currentUser;
  delete mergedInitialState.appStore.railsContext.organization;
  mergedInitialState.appStore = camelcaseKeys(mergedInitialState.appStore, {
    deep: true,
  });

  // const store = createStore(
  //   createRootReducer(history),
  //   mergedInitialState,
  //   composedEnhancers
  // );
  const store = configureStore({
    reducer: createRootReducer(history),
    middleware,
    preloadedState: mergedInitialState,
    // enhancers,
  });

  return {
    store,
    history,
  };
};

export const getEditableStates = (store) => {
  const { $$organizationStore, $$railsContextStore } = store;
  const canEditOrganization = organizationDuck.selectors.canEditOrganization(
    $$organizationStore,
    $$railsContextStore
  );
  const isPreviewingWebsite = railsContextDuck.selectors.isPreviewingWebsite(
    $$railsContextStore
  );
  const isEditingOrganization = canEditOrganization && !isPreviewingWebsite;
  return {
    canEditOrganization,
    isPreviewingWebsite,
    isEditingOrganization,
  };
};
